/** Speech recognition helper shared by the English corner and dialog shadowing (Web Speech API). */
export type Recognition = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void;
  onerror: () => void;
  onend: () => void;
  start(): void;
};

export function recognizer(): Recognition | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export function hasMic(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as Record<string, unknown>;
  return !!(w.SpeechRecognition ?? w.webkitSpeechRecognition);
}

/** Lowercase words without punctuation, for forgiving comparisons. */
export function words(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** Share of the target's words found in what was heard (0..1). */
export function overlap(target: string, heard: string): number {
  const t = words(target);
  if (!t.length) return 0;
  const hw = new Set(words(heard));
  return t.filter((w) => hw.has(w)).length / t.length;
}

/** Normalised comparison for dictation: case, punctuation and curly quotes don't matter. */
export function dictationScore(target: string, typed: string): 'ok' | 'close' | 'bad' {
  const a = words(target).join(' '), b = words(typed).join(' ');
  if (a === b) return 'ok';
  return overlap(target, typed) >= 0.6 || (a.length > 3 && editDistance(a, b) <= 2) ? 'close' : 'bad';
}

function editDistance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length][b.length];
}

/** Listen once for `target`; calls back with whether it matched well enough and the best transcript. */
export function listenFor(target: string, done: (ok: boolean, heard: string) => void, fail: () => void): boolean {
  const rec = recognizer();
  if (!rec) return false;
  rec.lang = 'en-US';
  rec.interimResults = false;
  rec.maxAlternatives = 5;
  let finished = false;
  rec.onresult = (e) => {
    finished = true;
    const alts = Array.from(e.results[0] ?? []).map((a) => a.transcript.trim());
    const best = alts.reduce((b, a) => (overlap(target, a) > overlap(target, b) ? a : b), alts[0] ?? '');
    done(overlap(target, best) >= (words(target).length <= 2 ? 1 : 0.7), best);
  };
  rec.onerror = () => {
    finished = true;
    fail();
  };
  rec.onend = () => {
    if (!finished) fail();
  };
  try {
    rec.start();
  } catch {
    fail();
  }
  return true;
}
